interface TrackPdpClinicChain {
  handle?: string
}

interface TrackPdpProduct {
  handle: string
  name?: string
  campaign?: { status?: string }
  idExternal?: string
  category?: { group?: string, name?: string, child?: string }
  categoriesNew?: string[]
  categoryIds?: string[]
  clinicChain?: TrackPdpClinicChain
  clinicChains?: TrackPdpClinicChain[]
}

function clinicChainsOf(product: TrackPdpProduct): TrackPdpClinicChain[] {
  return product.clinicChains ?? (product.clinicChain ? [product.clinicChain] : [])
}

interface TrackPdpViewItemInput {
  product: TrackPdpProduct
  price?: number
  fields?: Record<string, string>
  supplierName?: string
  reference?: 'office' | 'pharmacy'
  quantity?: number
}

/**
 * GA4 `view_item` tracker for the product detail page.
 */
export function usePdpViewItemTracking() {
  const gtm = useGtm()
  const helper = useHelper()
  const { isItemGpoCompliant } = useGpoCompliance()
  const { levelsFromCategoriesNew, levelsFromLegacyCategory, levelsFromCategoryIds } = useCategoryClickTracking()
  const pageSection = usePageSection()
  const { getPosition, getOriginSection } = useViewItemPosition()

  function resolveCategoryLevels(product: TrackPdpProduct): string[] {
    const idLevels = levelsFromCategoryIds(product.categoryIds ?? [])
    if (idLevels.length > 0) return idLevels
    const treeLevels = levelsFromCategoriesNew(product.categoriesNew ?? [])
    return treeLevels.length > 0 ? treeLevels : levelsFromLegacyCategory(product.category)
  }

  function trackPdpViewItem({ product, price, fields, supplierName, reference, quantity }: TrackPdpViewItemInput) {
    const ga4Item = helper.convertProductToGa4ViewItem({
      product,
      price,
      fields,
      supplierName,
      categoryLevels: resolveCategoryLevels(product),
      quantity,
      reference,
      isGpoRecommended: isItemGpoCompliant({ clinicChains: clinicChainsOf(product) }),
      index: getPosition(product.idExternal ?? ''),
    })

    const resolvedSection = getOriginSection() ?? pageSection

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'view_item',
      page_section: resolvedSection,
      ecommerce: {
        currency: 'EUR',
        value: Math.round((ga4Item.price ?? 0) * (ga4Item.quantity ?? 0) * 100) / 100,
        items: [ga4Item],
      },
    })
  }

  return { trackPdpViewItem }
}
